import { fetchWithOptions } from 'Utility/fetch';
import { parseJWTFromCookie } from 'Utility/jwtAuthTools';
import { ActionableErrorContext, Loggable, makeLog, UsageAnalyticDatapoint } from 'Utility/logger';

/**
 * Patient search by ssn for the purpose of some checks
 * @param {string} org - Org id
 * @param {string} ssn - Patient ssn
 * @returns {Promise<INeuroPersonData[]> | null} Matched patients
 */
export const patientSearch = async (org: string, ssn: string): Promise<INeuroPersonData[] | null> => {
  return fetchWithOptions(
    `/api/patient/v2/search/${org}?search=${ssn}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'GET' },
  )
    .then(
      (res: Response) => {
        if (res.status === 200) {
          return res.json() as Promise<INeuroPersonData[]>;
        } else {
          throw new Error('unexpected status ' + res.status);
        }
      },
      (error: unknown) => {
        throw error;
      },
    )
    .catch((err: unknown) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'patient search by SSN'));
      return null;
    });
};

/**
 * Patient selection
 * @param {string} patientid - Patient id
 * @returns {Promise<Response | null>} Response
 */
export const patientSelect = async (patientid: string): Promise<Response | null> => {
  return fetchWithOptions(
    `/api/patient/v2/select/`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'PATCH', body: JSON.stringify({ id: patientid }) },
  )
    .then(
      (res: Response) => {
        if (res.status === 200) {
          return res;
        } else {
          throw new Error('unexpected status ' + res.status);
        }
      },
      (error: unknown) => {
        throw error;
      },
    )
    .catch((err: unknown) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'PATCH patient select'));
      return null;
    });
};

/**
 * JWT modification
 * @param {string} resText - Response text
 * @returns {Promise<boolean>} Success
 */
export const jwtModify = async (resText: string): Promise<boolean> => {
  return fetchWithOptions(`/auth/modify`, { neurojwt: parseJWTFromCookie() }, { method: 'PATCH', body: resText })
    .then(
      (res: Response) => {
        if (res.status === 200) {
          return true;
        } else {
          throw new Error('unexpected status ' + res.status);
        }
      },
      (error: unknown) => {
        throw error;
      },
    )
    .catch((err: unknown) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'PATCH JWT'));
      return false;
    });
};

export const createVisitAndModifyAuth = (
  reason?: string | null,
  appointmentId?: string | null,
  platform?: Platform | null,
): Promise<void> =>
  fetchWithOptions(
    '/api/visit/create',
    { neurojwt: parseJWTFromCookie() },
    {
      method: 'POST',
      body: JSON.stringify({
        visitDate: Date.now(),
        visitReason: reason ?? null,
        visitExternalId: appointmentId ?? null,
      }),
    },
  )
    .then(
      (res: Response) => {
        if (res.status === 200) {
          return res.text().then((text: string) =>
            fetchWithOptions(`/auth/modify`, { neurojwt: parseJWTFromCookie() }, { method: 'PATCH', body: text }).then(
              (jwtRes: Response) => {
                if (jwtRes.status === 200) {
                  // Record entering patient data when visit has been created
                  makeLog(
                    Loggable.UsageAnalytic,
                    new UsageAnalyticDatapoint({
                      platformOpened: platform,
                    }),
                  );
                } else throw new Error('unexpected status ' + jwtRes.status);
              },
            ),
          );
        } else {
          throw new Error('unexpected status ' + res.status);
        }
      },
      (error: unknown) => {
        throw error;
      },
    )
    .catch((err: unknown) => makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'create visit')));
